import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import "./Navbar.css";

function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { totalItems } = useCart(); // Utilisation du contexte panier

  const navigate = useNavigate();
  const location = useLocation();

  // Vérification du statut de connexion à partir du localStorage
  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedIsAdmin = localStorage.getItem("isAdmin") === "true";
    const storedUserName = localStorage.getItem("userName");

    setIsLoggedIn(!!token);
    setIsAdmin(storedIsAdmin);
    setUserName(storedUserName || "");

    // Ferme les menus déroulants lorsque l'emplacement change
    setIsNavCollapsed(true);
    setIsUserMenuOpen(false);
  }, [location]);

  // Détection du défilement pour modifier l'apparence de la navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  // Détection de l'installabilité de l'application
  useEffect(() => {
    const handleInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", () => {
      setIsInstallable(false);
      window.deferredPrompt = null;
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, []);

  // Fonction pour installer l'application
  const handleInstallClick = async () => {
    if (!window.deferredPrompt) {
      console.log("Cannot install: No installation prompt available");
      return;
    }

    // Afficher le prompt d'installation
    window.deferredPrompt.prompt();

    // Attendre que l'utilisateur réponde au prompt
    const { outcome } = await window.deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);

    // Effacer le prompt différé
    window.deferredPrompt = null;
    setIsInstallable(false);
  };

  // Fonction de déconnexion
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("userName");
    setIsLoggedIn(false);
    setIsAdmin(false);
    setUserName("");
    navigate("/login");
  };

  // Fonction de recherche
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsNavCollapsed(true);
    }
  };

  // Toggle du menu utilisateur
  const toggleUserMenu = (e) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Gestionnaire pour le clic sur le backdrop du menu
  const handleBackdropClick = () => {
    setIsUserMenuOpen(false);
    setIsNavCollapsed(true);
  };

  // Fermer les menus au clic en dehors
  useEffect(() => {
    const handleClickOutside = () => {
      setIsUserMenuOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <header className="header-wrapper">
      {/* Backdrop pour les menus mobiles */}
      {(!isNavCollapsed || isUserMenuOpen) && (
        <div
          className="navbar-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        ></div>
      )}

      {/* Bande d'annonce supérieure */}
      <div className="announcement-bar text-white py-2">
        <div className="container">
          <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
            <span className="announcement-text">
              <i className="bi bi-truck me-1"></i> Livraison gratuite en
              Belgique dès 50€ d'achat
            </span>
            <span className="announcement-divider mx-2 d-none d-md-inline">
              |
            </span>
            <span className="announcement-text d-none d-md-inline">
              <i className="bi bi-gift me-1"></i> -10% sur votre première
              commande avec le code{" "}
              <strong className="announcement-highlight">BIENVENUE</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Barre de navigation principale */}
      <nav
        className={`navbar navbar-expand-lg navbar-light bg-white ${
          scrolled ? "navbar-scrolled shadow-sm" : ""
        }`}
      >
        <div className="container">
          {/* Logo */}
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <img
              src="/logo192.png"
              alt="BoxPlaisir Logo"
              className="navbar-logo me-2"
              width="35"
              height="35"
            />
            <span className="brand-text fw-bold">
              <span className="brand-box">Box</span>
              <span className="brand-plaisir">Plaisir</span>
            </span>
          </Link>

          {/* Boutons mobiles (panier et menu) */}
          <div className="navbar-mobile-buttons d-flex d-lg-none ms-auto me-2">
            {/* Bouton panier mobile */}
            <Link
              to="/cart"
              className="nav-link position-relative me-3"
              aria-label="Panier"
            >
              <i className="bi bi-cart fs-5 nav-icon"></i>
              {totalItems > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill badge-cart">
                  {totalItems}
                </span>
              )}
            </Link>

            {/* Bouton utilisateur mobile - uniquement si connecté */}
            {isLoggedIn && (
              <div className="nav-item dropdown me-3">
                <button
                  className="btn nav-link dropdown-toggle p-0"
                  type="button"
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  aria-label="Menu utilisateur"
                >
                  <i className="bi bi-person-circle fs-5 nav-icon"></i>
                </button>
                <ul
                  className={`dropdown-menu dropdown-menu-end user-dropdown ${
                    isUserMenuOpen ? "show" : ""
                  }`}
                >
                  <li className="dropdown-header text-center pb-0">
                    <span className="d-block text-primary fw-bold">
                      {userName || "Mon compte"}
                    </span>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="bi bi-person me-2"></i>Profil
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      to="/orders"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="bi bi-box me-2"></i>Mes commandes
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      to="/favorites"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="bi bi-heart me-2"></i>Mes favoris
                    </Link>
                  </li>
                  {isAdmin && (
                    <>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <Link
                          className="dropdown-item"
                          to="/admin"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="bi bi-speedometer2 me-2"></i>
                          Administration
                        </Link>
                      </li>
                    </>
                  )}
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>Déconnexion
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Bouton toggle du menu hamburger */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
            aria-controls="navbarContent"
            aria-expanded={!isNavCollapsed}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Contenu principal de la navbar qui se collapse sur mobile */}
          <div
            className={`${isNavCollapsed ? "collapse" : ""} navbar-collapse`}
            id="navbarContent"
          >
            {/* Liens de navigation */}
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname === "/" ? "active" : ""
                  }`}
                  to="/"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  Accueil
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname.includes("/category") ? "active" : ""
                  }`}
                  to="/category"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  Nos box
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname === "/how-it-works" ? "active" : ""
                  }`}
                  to="/how-it-works"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  Comment ça marche
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname === "/about" ? "active" : ""
                  }`}
                  to="/about"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  À propos
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname === "/contact" ? "active" : ""
                  }`}
                  to="/contact"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  Contact
                </Link>
              </li>
            </ul>

            {/* Partie droite de la navbar: recherche, panier, compte */}
            <div className="navbar-right d-flex align-items-center flex-column flex-lg-row">
              {/* Formulaire de recherche */}
              <form
                className="d-flex me-lg-2 mb-3 mb-lg-0 w-100"
                onSubmit={handleSearch}
              >
                <div className="input-group search-group w-100">
                  <input
                    type="search"
                    className="form-control search-input"
                    placeholder="Rechercher..."
                    aria-label="Recherche"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    className="btn btn-search"
                    type="submit"
                    aria-label="Bouton de recherche"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>

              {/* Bouton d'installation de PWA (si disponible) */}
              {isInstallable && (
                <button
                  className="btn btn-sm btn-outline-primary me-2 mb-3 mb-lg-0 d-none d-lg-flex align-items-center"
                  onClick={handleInstallClick}
                >
                  <i className="bi bi-download me-2"></i>
                  <span className="d-none d-xl-inline">
                    Installer l'application
                  </span>
                </button>
              )}

              {/* Bouton panier */}
              <Link
                to="/cart"
                className="nav-link position-relative me-3 d-none d-lg-block"
                aria-label="Voir le panier"
              >
                <i className="bi bi-cart fs-5 nav-icon"></i>
                {totalItems > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill badge-cart">
                    {totalItems}
                    <span className="visually-hidden">
                      articles dans le panier
                    </span>
                  </span>
                )}
              </Link>

              {/* Menu utilisateur (connecté) ou boutons de connexion/inscription */}
              {isLoggedIn ? (
                <div className="nav-item dropdown d-none d-lg-block">
                  <button
                    className="btn nav-link dropdown-toggle d-flex align-items-center"
                    type="button"
                    onClick={toggleUserMenu}
                    aria-expanded={isUserMenuOpen}
                    aria-label="Menu utilisateur"
                  >
                    <i className="bi bi-person-circle me-2 fs-5 nav-icon"></i>
                    <span className="d-none d-xl-inline">
                      {userName || "Mon compte"}
                    </span>
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end user-dropdown ${
                      isUserMenuOpen ? "show" : ""
                    }`}
                  >
                    <li className="dropdown-header text-center pb-0">
                      {userName && (
                        <span className="d-block text-primary fw-bold">
                          {userName}
                        </span>
                      )}
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="bi bi-person me-2"></i>Profil
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/orders"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="bi bi-box me-2"></i>Mes commandes
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/favorites"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="bi bi-heart me-2"></i>Mes favoris
                      </Link>
                    </li>
                    {isAdmin && (
                      <>
                        <li>
                          <hr className="dropdown-divider" />
                        </li>
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/admin"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="bi bi-speedometer2 me-2"></i>
                            Administration
                          </Link>
                        </li>
                      </>
                    )}
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={handleLogout}>
                        <i className="bi bi-box-arrow-right me-2"></i>
                        Déconnexion
                      </button>
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="d-none d-lg-flex auth-buttons">
                  <Link to="/login" className="btn btn-outline-primary me-2">
                    Connexion
                  </Link>
                  <Link to="/register" className="btn btn-primary">
                    Inscription
                  </Link>
                </div>
              )}

              {/* Menu de connexion/inscription sur mobile (uniquement si non connecté) */}
              {!isLoggedIn && (
                <div className="d-flex d-lg-none flex-column mt-3 w-100">
                  <Link
                    to="/login"
                    className="btn btn-outline-primary mb-2"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    <i className="bi bi-person-plus me-2"></i>
                    Inscription
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;